'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Box } from '@mui/material';
import {
  clampPanelCompactHeight,
  getMaxCompactPanelHeight,
  PANEL_COMPACT_HEIGHT_MIN,
} from '@/lib/panel-resize';

const KEYBOARD_STEP = 16;
const KEYBOARD_BIG_STEP = 80;

function resolveEffectiveMax(maxHeight: number | undefined): number {
  return (
    maxHeight ??
    (typeof window !== 'undefined'
      ? getMaxCompactPanelHeight(window.innerHeight)
      : 800)
  );
}

export interface PanelHeightResizeHandleProps {
  value: number;
  onHeightChange: (height: number) => void;
  ariaLabel: string;
  maxHeight?: number;
  /** Gap between panel bottom and viewport bottom (compact floating mode). */
  bottomInset?: number;
  /** When false the handle is pointer-only (no tab stop, hidden from AT). */
  keyboardResizable?: boolean;
  /** Override for aria-valuetext; defaults to English "{n} pixels tall…". */
  formatValueText?: (value: number) => string;
}

export default function PanelHeightResizeHandle({
  value,
  onHeightChange,
  ariaLabel,
  maxHeight,
  bottomInset = 0,
  keyboardResizable = true,
  formatValueText,
}: PanelHeightResizeHandleProps) {
  const [isDragging, setIsDragging] = useState(false);
  const effectiveMax = resolveEffectiveMax(maxHeight);
  const clampedValue = clampPanelCompactHeight(value, effectiveMax);

  const clearDragStyles = useCallback(() => {
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  // Read max at call time (not via a render-derived dep) so useCallback stays
  // valid under react-hooks/preserve-manual-memoization.
  const applyHeight = useCallback(
    (raw: number) => {
      const h = clampPanelCompactHeight(raw, resolveEffectiveMax(maxHeight));
      onHeightChange(h);
    },
    [maxHeight, onHeightChange],
  );

  const handlePointerMove = useCallback(
    (clientY: number) => {
      if (typeof window === 'undefined') return;
      // Bottom-anchored panel: height grows upward from the viewport bottom inset.
      applyHeight(window.innerHeight - clientY - bottomInset);
    },
    [applyHeight, bottomInset],
  );

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      handlePointerMove(e.clientY);
    };
    const handleMouseUp = () => {
      setIsDragging(false);
      clearDragStyles();
    };
    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      const touch = e.touches[0];
      handlePointerMove(touch.clientY);
    };
    const handleTouchEnd = () => {
      setIsDragging(false);
      clearDragStyles();
    };

    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      clearDragStyles();
    };
  }, [isDragging, handlePointerMove, clearDragStyles]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleTouchStart = () => setIsDragging(true);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const step = e.shiftKey ? KEYBOARD_BIG_STEP : KEYBOARD_STEP;
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      applyHeight(clampedValue + step);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      applyHeight(clampedValue - step);
    }
  };

  return (
    <Box
      {...(keyboardResizable
        ? {
            role: 'separator',
            tabIndex: 0,
            'aria-orientation': 'horizontal' as const,
            'aria-label': ariaLabel,
            'aria-valuenow': clampedValue,
            'aria-valuemin': PANEL_COMPACT_HEIGHT_MIN,
            'aria-valuemax': effectiveMax,
            'aria-valuetext': formatValueText
              ? formatValueText(clampedValue)
              : `${clampedValue} pixels tall, use arrow keys to resize`,
          }
        : { 'aria-hidden': true })}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onKeyDown={handleKeyDown}
      sx={{
        position: 'absolute',
        top: -2,
        left: 0,
        right: 0,
        height: 4,
        cursor: 'row-resize',
        zIndex: 20,
        '&:focus': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: -2,
        },
        '&:hover > .panel-height-resize-strip, &:focus > .panel-height-resize-strip': {
          bgcolor: 'primary.main',
          opacity: 0.5,
        },
      }}
    >
      <Box
        className="panel-height-resize-strip"
        sx={{
          position: 'absolute',
          top: 2,
          left: 0,
          right: 0,
          height: 1,
          bgcolor: isDragging ? 'primary.main' : 'divider',
          opacity: isDragging ? 1 : 0,
          transition: 'background-color 0.2s, opacity 0.2s',
        }}
      />
    </Box>
  );
}
